/**
 * Catálogo de variables que se pueden usar en los textos libres de los
 * documentos (`{{cliente.nombre}}`). Cada variable indica en qué documentos
 * tiene valor: la validación de la configuración rechaza una variable que no
 * aplica al documento donde se escribió.
 */
import { DOCUMENT_KINDS } from './catalog.js';
/** Documentos dirigidos a un cliente sobre un préstamo. */
const CLIENT_DOCUMENTS = [
    'loanContract',
    'paymentPlan',
    'paymentReceipt',
    'accountStatement',
    'paidOffCertificate',
    'disbursementVoucher',
];
/** Documentos que incluyen las condiciones del préstamo. */
const LOAN_TERMS_DOCUMENTS = [
    'loanContract',
    'paymentPlan',
    'accountStatement',
    'paidOffCertificate',
    'disbursementVoucher',
];
/** Documentos emitidos en una agencia concreta. */
const BRANCH_DOCUMENTS = ['paymentReceipt', 'disbursementVoucher', 'cashClosing'];
/** Catálogo completo de variables, agrupado por tema. */
export const TEMPLATE_VARIABLES = [
    // Institución y emisión: en todos los documentos
    { key: 'institucion.nombre', description: 'Nombre comercial de la institución', example: 'Crédito Xelajú', kinds: DOCUMENT_KINDS },
    { key: 'institucion.razon_social', description: 'Razón social de la institución', example: 'Asociación de Desarrollo Integral Xelajú', kinds: DOCUMENT_KINDS },
    { key: 'institucion.nit', description: 'NIT de la institución', example: '1234567-8', kinds: DOCUMENT_KINDS },
    { key: 'institucion.direccion', description: 'Dirección de la institución', example: '14 Avenida 5-20 Zona 1, Quetzaltenango', kinds: DOCUMENT_KINDS },
    { key: 'institucion.telefono', description: 'Teléfono de la institución', example: '7765 4321', kinds: DOCUMENT_KINDS },
    { key: 'fecha', description: 'Fecha de emisión del documento, en letras', example: '26 de septiembre de 2026', kinds: DOCUMENT_KINDS },
    { key: 'representante.nombre', description: 'Representante legal (configurado en Datos legales)', example: 'Ana Lucía Morales Pérez', kinds: DOCUMENT_KINDS },
    { key: 'representante.cargo', description: 'Cargo del representante legal', example: 'Representante legal', kinds: DOCUMENT_KINDS },
    { key: 'lugar', description: 'Lugar de firma o de emisión (municipio y departamento)', example: 'Quetzaltenango, Quetzaltenango', kinds: DOCUMENT_KINDS },
    // Cliente y préstamo
    { key: 'cliente.nombre', description: 'Nombre completo del cliente', example: 'María Cristina Tzul Ixcoy', kinds: CLIENT_DOCUMENTS },
    { key: 'cliente.dpi', description: 'DPI (CUI) del cliente', example: '2345 67890 0901', kinds: CLIENT_DOCUMENTS },
    { key: 'cliente.codigo', description: 'Código interno del cliente', example: 'CLI-000123', kinds: CLIENT_DOCUMENTS },
    { key: 'prestamo.numero', description: 'Número del préstamo', example: 'PR-2026-000451', kinds: CLIENT_DOCUMENTS },
    { key: 'monto', description: 'Monto principal del documento: el préstamo, el pago o el neto desembolsado', example: 'Q 15,000.00', kinds: CLIENT_DOCUMENTS },
    { key: 'prestamo.monto', description: 'Monto otorgado', example: 'Q 15,000.00', kinds: LOAN_TERMS_DOCUMENTS },
    { key: 'prestamo.monto_letras', description: 'Monto otorgado en letras', example: 'QUINCE MIL QUETZALES CON 00/100', kinds: LOAN_TERMS_DOCUMENTS },
    { key: 'prestamo.tasa', description: 'Tasa de interés anual nominal', example: '36.00 %', kinds: LOAN_TERMS_DOCUMENTS },
    { key: 'prestamo.plazo', description: 'Plazo en cuotas', example: '12 cuotas', kinds: LOAN_TERMS_DOCUMENTS },
    { key: 'prestamo.frecuencia', description: 'Frecuencia de pago', example: 'mensual', kinds: LOAN_TERMS_DOCUMENTS },
    { key: 'prestamo.vencimiento', description: 'Fecha de la última cuota', example: '28 de septiembre de 2027', kinds: LOAN_TERMS_DOCUMENTS },
    // Recibo
    { key: 'pago.recibo', description: 'Número de recibo', example: 'RC-00001234', kinds: ['paymentReceipt'] },
    { key: 'pago.fecha', description: 'Fecha del pago', example: '26 de septiembre de 2026', kinds: ['paymentReceipt'] },
    { key: 'pago.forma', description: 'Forma de pago', example: 'Efectivo', kinds: ['paymentReceipt'] },
    // Agencia y caja
    { key: 'agencia.nombre', description: 'Agencia donde se emite', example: 'Agencia Central', kinds: BRANCH_DOCUMENTS },
    { key: 'caja.cajero', description: 'Nombre del cajero', example: 'José Ixcoy', kinds: ['cashClosing'] },
    { key: 'caja.fecha', description: 'Fecha operativa de la caja', example: '26 de septiembre de 2026', kinds: ['cashClosing'] },
    // Reportes
    { key: 'reporte.titulo', description: 'Título del reporte impreso', example: 'Cartera en mora', kinds: ['tabularReport'] },
];
/** Variables que tienen valor en todos los documentos (para encabezado, pie y marca de agua comunes). */
export const GLOBAL_TEMPLATE_VARIABLES = TEMPLATE_VARIABLES.filter((variable) => variable.kinds.length === DOCUMENT_KINDS.length);
/** Variables disponibles en un documento. */
export function templateVariablesFor(kind) {
    return TEMPLATE_VARIABLES.filter((variable) => variable.kinds.includes(kind));
}
/** Nombres de las variables disponibles en un documento (o en todos, si no se indica). */
export function allowedTemplateVariables(kind) {
    const variables = kind ? templateVariablesFor(kind) : GLOBAL_TEMPLATE_VARIABLES;
    return new Set(variables.map((variable) => variable.key));
}
/** Valores de muestra de todas las variables (vista previa). */
export function sampleTemplateValues() {
    return Object.fromEntries(TEMPLATE_VARIABLES.map((variable) => [variable.key, variable.example]));
}
//# sourceMappingURL=variables.js.map